import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';

export const useAuthHelper = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [validateTokenResponse, setValidateTokenResponse] = useState<any>(null);
  const navigate = useNavigate();
  
  // Function to validate token with the API wrapped in useCallback
  const validateToken = useCallback(async (storedToken: string | null) => {
    console.log('Validating token...', storedToken);
    if (!storedToken) {
      setIsAuthenticated(false);
      setIsLoading(false);
      return false;
    }
    
    try {
      setIsLoading(true);
      
      // Make direct API call using axios
      const response = await apiClient.get(
        `/users/redirect/${encodeURIComponent(storedToken)}`,
        {
          headers: {
            'Authorization': `${storedToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const responseData = response.data;
      setValidateTokenResponse(responseData);

      console.log('Response data:', responseData);
      
      if (!responseData) {
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
        return false;
      }

      if (responseData && responseData.message === 'token_decode_success') {
        setIsAuthenticated(true);
        return true;
      } else {
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
        return false;
      }
    } catch (error: any) {
      // Error validating token
      console.error('Token validation error:', error.message || error);
      localStorage.removeItem('token');
      setIsAuthenticated(false);
      navigate('/login', { replace: true });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [navigate, setIsAuthenticated, setIsLoading, setValidateTokenResponse]);

  // Initialize token from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    setToken(storedToken);
    validateToken(storedToken);
  }, [validateToken]);

  // Login function
  const login = async (newToken: string) => {
    try {
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setIsAuthenticated(true);
      navigate('/store', { replace: true });
      return true;
    } catch (error: any) {
      console.error('Login error:', error.message || error);
      return false;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Optional: Call logout endpoint if you have one
      if (token) {
        await apiClient.put('/users/logout', {}, {
          headers: {
            'Authorization': `${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error: any) {
      console.error('Logout error:', error.message || error);
    } finally {
      // Always clear local state even if API call fails
      localStorage.removeItem('token');
      setToken(null);
      setIsAuthenticated(false);
      navigate('/login', { replace: true });
    }
  };

  return {
    isAuthenticated,
    isLoading,
    token,
    validateTokenResponse,
    login,
    logout,
    validateToken
  };
};
